import "~/css/redesign-v1/tokens.css";

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import Modal from "react-modal";

import { featureFlags } from "~/common/feature-flags";
import { Button as RedesignButton } from "~/common/redesign-v1/ui/button";
import { Textarea as RedesignTextarea } from "~/common/redesign-v1/ui/textarea";
import { Button as LegacyButton } from "~/components/ui/button";
import { TextArea as LegacyTextarea } from "~/components/ui/textarea";
import { buildPlanApproveDisclaimer } from "~/features/plan-approve/disclaimer";

import { EmbeddedViewer } from "./embedded-viewer";
import type { DoctorViewerApi, PlanComment, ViewerPlan } from "./types";
import styles from "./viewer.module.css";

type Decision = "approve" | "rework";
type Props = {
  patientId: number;
  initialPlans: ViewerPlan[];
  initialPlanId?: number;
  api: DoctorViewerApi;
  doctorName: string;
  clinicTitle: string;
  onRefreshPatient: () => Promise<void>;
  onPlansChanged: (plans: ViewerPlan[]) => void;
  onClose: () => void;
};

export function ReviewDialog({ patientId, initialPlans, initialPlanId, api, doctorName, clinicTitle, onRefreshPatient, onPlansChanged, onClose }: Props) {
  const intl = useIntl();
  const FormButton = featureFlags.redesignV1 ? RedesignButton : LegacyButton;
  const FormTextarea = featureFlags.redesignV1 ? RedesignTextarea : LegacyTextarea;
  const message = (id: FormatjsIntl.Message["ids"]) => intl.formatMessage({ id });
  const [plans, setPlans] = useState(initialPlans);
  const [selectedId, setSelectedId] = useState(initialPlanId ?? initialPlans[0]?.id);
  const plan = plans.find((item) => item.id === selectedId);
  const [comments, setComments] = useState<PlanComment[]>([]);
  const [commentsLoading, setCommentsLoading] = useState(true);
  const [commentsFailed, setCommentsFailed] = useState(false);
  const [commentRevision, setCommentRevision] = useState(0);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [refreshRequired, setRefreshRequired] = useState(false);
  const [error, setError] = useState(false);
  const resolver = useRef<(() => void) | null>(null);
  const mutationPending = useRef(false);
  const form = useForm<{ comment: string; accepted: boolean }>({ defaultValues: { comment: "", accepted: false } });

  useEffect(() => {
    const elements = [document.documentElement, document.body];
    const overflow = elements.map((element) => element.style.overflow);
    for (const element of elements) element.style.overflow = "hidden";
    return () => {
      elements.forEach((element, index) => {
        element.style.overflow = overflow[index] ?? "";
      });
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setComments([]);
    setCommentsFailed(false);
    setCommentsLoading(true);
    if (selectedId !== undefined) {
      void api
        .comments(patientId, selectedId, controller.signal)
        .then((result) => {
          if (!controller.signal.aborted) setComments(result);
        })
        .catch(() => {
          if (!controller.signal.aborted) setCommentsFailed(true);
        })
        .finally(() => {
          if (!controller.signal.aborted) setCommentsLoading(false);
        });
    } else setCommentsLoading(false);
    return () => controller.abort();
  }, [api, patientId, selectedId, commentRevision]);
  useEffect(
    () => () => {
      resolver.current?.();
    },
    [],
  );

  const requestDecision = useCallback(
    (next: Decision) => {
      if (mutationPending.current) return Promise.resolve();
      form.reset();
      setError(false);
      setDecision(next);
      return new Promise<void>((resolve) => {
        resolver.current = resolve;
      });
    },
    [form],
  );
  const cancelDecision = () => {
    if (mutationPending.current) return;
    setDecision(null);
    resolver.current?.();
    resolver.current = null;
  };
  const close = useCallback(() => {
    if (mutationPending.current) return;
    resolver.current?.();
    resolver.current = null;
    onClose();
  }, [onClose]);
  const options = useMemo(
    () =>
      plan
        ? {
            manifestUrl: plan.manifest_url,
            patientId,
            correctionIndex: plan.correction_index,
            version: plan.version,
            lang: intl.locale === "ru" ? ("ru" as const) : ("en" as const),
            actions: { approve: plan.actions.can_approve && !refreshRequired, rework: plan.actions.can_rework && !refreshRequired, report: true, close: true },
            handlers: {
              approve: () => requestDecision("approve"),
              rework: () => requestDecision("rework"),
              report: () => {
                window.open(`/pages/patient/${patientId}/report?plan=${plan.id}`, "_blank", "noopener");
              },
              close,
            },
          }
        : null,
    [plan, patientId, intl.locale, refreshRequired, requestDecision, close],
  );

  async function refresh() {
    // A completed decision must not remain actionable if refreshing fails.
    setRefreshRequired(true);
    const nextPlans = await api.list(patientId);
    await onRefreshPatient();
    setPlans(nextPlans);
    onPlansChanged(nextPlans);
    setSelectedId((id) => (nextPlans.some((item) => item.id === id) ? id : nextPlans[0]?.id));
    setCommentRevision((value) => value + 1);
    setRefreshRequired(false);
  }
  const submitDecision = form.handleSubmit(async ({ comment }) => {
    if (!plan || !decision || mutationPending.current) return;
    const allowed = decision === "approve" ? plan.actions.can_approve : plan.actions.can_rework;
    if (!allowed || refreshRequired) return;
    mutationPending.current = true;
    setSubmitting(true);
    setError(false);
    try {
      if (decision === "approve") await api.approve(patientId, plan.id);
      else await api.rework(patientId, plan.id, comment.trim());
      setDecision(null);
      resolver.current?.();
      resolver.current = null;
      await refresh();
    } catch {
      setError(true);
    } finally {
      mutationPending.current = false;
      setSubmitting(false);
    }
  });

  return (
    <Modal
      isOpen
      contentLabel={message("viewer.title")}
      appElement={document.getElementById("react_root") ?? undefined}
      ariaHideApp={Boolean(document.getElementById("react_root"))}
      onRequestClose={close}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={!submitting}
      overlayClassName={styles["overlay"]}
      className={styles["dialog"]}
    >
      {error && !decision ? <div role="alert">{message("viewer.action.error")}</div> : null}
      {refreshRequired ? (
        <div role="alert">
          {message("viewer.refresh.required")}{" "}
          <button
            type="button"
            disabled={submitting}
            onClick={() => {
              setSubmitting(true);
              void refresh()
                .then(() => setError(false))
                .catch(() => setError(true))
                .finally(() => setSubmitting(false));
            }}
          >
            {message("viewer.retry")}
          </button>
        </div>
      ) : null}
      <div className={styles["layout"]} aria-hidden={decision ? true : undefined}>
        {options ? <EmbeddedViewer options={options} /> : <div />}
      </div>
      {decision ? (
        <Modal
          isOpen
          contentLabel={message(`viewer.${decision}`)}
          ariaHideApp={false}
          onRequestClose={cancelDecision}
          onAfterOpen={() => form.setFocus(decision === "approve" ? "accepted" : "comment")}
          shouldCloseOnOverlayClick={!submitting}
          shouldCloseOnEsc={!submitting}
          overlayClassName={styles["decisionOverlay"]}
          className={`${styles["dialog"]} ${styles["panel"]} ${styles["decisionDialog"]}`}
        >
          <div data-redesign-v1={featureFlags.redesignV1 ? "" : undefined}>
            {error ? <div role="alert">{message("viewer.action.error")}</div> : null}
            <form
              className={styles["form"]}
              onSubmit={(event) => {
                void submitDecision(event);
              }}
            >
              {decision === "approve" ? <h3>{message("viewer.approve")}</h3> : null}
              {decision === "approve" ? (
                <>
                  <div
                    className={styles["disclaimer"]}
                    // The legal text scrolls; keyboard users must be able to focus the region.
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                    tabIndex={0}
                    role="region"
                    aria-label={message("viewer.consent")}
                  >
                    {buildPlanApproveDisclaimer(doctorName, clinicTitle)[intl.locale]}
                  </div>
                  {doctorName && clinicTitle ? (
                    <label>
                      <input type="checkbox" {...form.register("accepted", { required: true })} disabled={submitting} /> {message("viewer.consent")}
                    </label>
                  ) : (
                    <p role="alert">{message("viewer.approve.profile.required")}</p>
                  )}
                </>
              ) : (
                <label className={styles["field"]}>
                  {message("viewer.remarks")}
                  <FormTextarea
                    {...form.register("comment", { validate: (value) => value.trim().length > 0 })}
                    className={styles["remarks"]}
                    rows={6}
                    error={Boolean(form.formState.errors.comment)}
                    aria-invalid={Boolean(form.formState.errors.comment)}
                    aria-describedby={form.formState.errors.comment ? "viewer-remarks-error" : undefined}
                    disabled={submitting}
                  />
                </label>
              )}
              {form.formState.errors.comment ? (
                <span id="viewer-remarks-error" className={styles["fieldError"]} role="alert">
                  {message("viewer.comment.required")}
                </span>
              ) : null}
              <div className={styles["buttons"]}>
                <FormButton type="submit" className="tw-h-10" disabled={submitting || (decision === "approve" && (!form.watch("accepted") || !doctorName || !clinicTitle))}>
                  {message(decision === "rework" ? "viewer.rework.submit" : "viewer.submit")}
                </FormButton>
                <FormButton type="button" variant="secondary" className="tw-h-10" disabled={submitting} onClick={cancelDecision}>
                  {message("viewer.cancel")}
                </FormButton>
              </div>
            </form>
            {decision === "rework" && (commentsLoading || commentsFailed || comments.length > 0) ? (
              <section className={styles["history"]}>
                <h3>{message("viewer.comments")}</h3>
                {commentsLoading ? <p role="status">{message("viewer.loading")}</p> : null}
                {commentsFailed ? (
                  <p role="alert">
                    {message("viewer.comments.error")}{" "}
                    <button type="button" onClick={() => setCommentRevision((value) => value + 1)}>
                      {message("viewer.retry")}
                    </button>
                  </p>
                ) : null}
                {comments.map((item) => (
                  <article key={item.id}>
                    <strong>{item.author}</strong>
                    <time dateTime={item.created_at}>
                      {intl.formatDate(item.created_at)} {intl.formatTime(item.created_at)}
                    </time>
                    {item.correction_index !== undefined && item.version !== undefined ? (
                      <p>{intl.formatMessage({ id: "viewer.version.option" }, { correction: item.correction_index, version: item.version })}</p>
                    ) : null}
                    <p>{item.text}</p>
                  </article>
                ))}
              </section>
            ) : null}
          </div>
        </Modal>
      ) : null}
    </Modal>
  );
}
